import React from "react";
import moment from "moment";
import { Grid, Box, Typography, Stack, alpha, useTheme, Card, CardContent } from "@mui/material";
import { keyframes } from "@mui/system";
import { blue, orange, green, grey } from "@mui/material/colors";
import useScheduleUser from "../../../hooks/useScheduleUser";
import {
  AccountCircle as AccountCircleIcon,
  Badge as BadgeIcon,
  Numbers as NumbersIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Public as MapIcon,
  Store as StoreIcon,
  LocationCity as LocationCityIcon,
  Wifi as WifiIcon,
  ToggleOn as ToggleOnIcon,
  SignalCellularAlt as SignalCellularAltIcon,
  EventBusy as EventBusyIcon,
  PanTool as PanToolIcon,
  Schedule as ScheduleIcon,
  Fingerprint as FingerprintIcon,
  AssignmentInd as AssignmentIndIcon,
  Domain as DomainIcon,
  BusinessCenter as BusinessCenterIcon,
  Security as SecurityIcon,
  CalendarToday as CalendarTodayIcon,
  History as HistoryIcon,
  Update as UpdateIcon,
  ContactPage as ContactPageIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
// Using CSS-based watermarks for a lighter, more professional feel as requested
import calendarPastel from "../../../assets/illustrations/3d_calendar_pastel.svg";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px) rotate(-8deg); }
  50% { transform: translateY(-10px) rotate(-6deg); }
  100% { transform: translateY(0px) rotate(-8deg); }
`;

const floatStatus = keyframes`
  0% { transform: translateY(0px) rotate(-12deg); }
  50% { transform: translateY(-10px) rotate(-10deg); }
  100% { transform: translateY(0px) rotate(-12deg); }
`;

const onHoldDate = (startDate, endDate) => {
  if (!startDate || !endDate) return "---";
  return `${moment(startDate).format("DD MMM YYYY")} - ${moment(endDate).format("DD MMM YYYY")}`;
};

const SectionTitle = ({ title, icon: Icon, color = "primary", centered = false }) => {
  const theme = useTheme();
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;
  const darkColor = theme.palette[color]?.dark || theme.palette.primary.dark;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: centered ? "center" : "flex-start",
        gap: 2,
        mb: 3,
        p: 1.5,
        width: "100%",
        borderRadius: "12px",
        background: `linear-gradient(135deg, ${alpha(mainColor, 0.3)} 0%, ${alpha(mainColor, 0.1)} 100%)`,
        border: `1px solid ${alpha(mainColor, 0.4)}`,
        boxShadow: `0 4px 12px ${alpha(mainColor, 0.15)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 38,
          height: 38,
          borderRadius: "10px",
          bgcolor: "white",
          boxShadow: `0 4px 10px ${alpha(mainColor, 0.2)}`,
        }}
      >
        <Icon sx={{ fontSize: "1.3rem", color: mainColor }} />
      </Box>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 900,
          letterSpacing: "0.02em",
          color: darkColor,
          fontSize: "0.95rem",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const PropertyTile = ({ label, value, icon: Icon, highlight, color = "primary", plain }) => {
  const theme = useTheme();
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

  return (
    <Box
      sx={{
        p: plain ? 1.5 : 2,
        borderRadius: "18px",
        bgcolor: "transparent",
        border: plain ? "none" : `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "0%",
          bgcolor: mainColor,
          transition: "height 0.3s ease",
          borderRadius: "0 4px 4px 0",
        },
        "&:hover": {
          bgcolor: plain ? "transparent" : alpha(theme.palette.background.paper, 1),
          transform: plain ? "none" : "translateY(-4px)",
          boxShadow: plain ? "none" : "0 12px 24px rgba(0,0,0,0.04)",
          borderColor: alpha(mainColor, 0.2),
          "&::before": {
            height: plain ? "0%" : "100%",
          },
          "& .tile-icon": {
            color: mainColor,
            transform: "scale(1.1)",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.65rem",
          }}
        >
          {label}
        </Typography>
        {Icon && (
          <Icon
            className="tile-icon"
            sx={{
              fontSize: "1rem",
              color: alpha(theme.palette.text.secondary, 0.3),
              transition: "all 0.3s ease",
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          color: highlight ? mainColor : "text.primary",
          fontWeight: 900,
          fontSize: "0.95rem",
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {value || "---"}
      </Typography>
    </Box>
  );
};

const StatusIndicator = ({ status, type = "connection" }) => {
  const theme = useTheme();
  const getStatusConfig = (s) => {
    const normalized = s?.toLowerCase();
    if (normalized === "online") return { color: "#22c55e", label: "ONLINE", glow: true };
    if (normalized === "offline") return { color: "#cbd5e1", label: "OFFLINE", glow: false };
    if (normalized === "active") return { color: "#3b82f6", label: "ACTIVE", glow: true };
    if (normalized === "expired") return { color: "#f97316", label: "EXPIRED", glow: false };
    if (normalized === "suspended") return { color: "#ef4444", label: "SUSPENDED", glow: false };
    if (normalized === "on_hold" || normalized === "hold")
      return { color: "#eab308", label: "ON HOLD", glow: false };
    return { color: theme.palette.primary.main, label: s || "UNKNOWN", glow: false };
  };

  const config = getStatusConfig(status);

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.2,
        py: 0.5,
        borderRadius: "8px",
        bgcolor: alpha(config.color, 0.1),
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: config.color,
          boxShadow: config.glow ? `0 0 8px ${config.color}` : "none",
        }}
      />
      <Typography
        sx={{
          color: config.color,
          fontWeight: 900,
          fontSize: "0.75rem",
          letterSpacing: "0.02em",
        }}
      >
        {config.label}
      </Typography>
    </Box>
  );
};

export const IdentityWidget = ({ user }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: "28px",
        bgcolor: "white",
        border: `2px solid ${alpha(theme.palette.success.main, 0.4)}`,
        boxShadow: `0 12px 40px ${alpha(theme.palette.success.main, 0.15)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          boxShadow: `0 16px 50px ${alpha(theme.palette.success.main, 0.25)}`,
          border: `2px solid ${theme.palette.success.main}`,
          transform: "translateY(-4px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        animation: `${fadeInUp} 0.6s ease-out forwards`,
      }}
    >
      <SectionTitle title="Identity" icon={FingerprintIcon} color="success" centered />
      <Grid container spacing={0} sx={{ flexGrow: 1, position: "relative", zIndex: 1 }}>
        <Grid item xs={7}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <Box
              sx={{
                pb: 2,
                mb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: "text.secondary",
                  fontSize: "0.65rem",
                  mb: 0.5,
                  display: "block",
                  letterSpacing: "0.05em",
                }}
              >
                ACCOUNT NUMBER
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main" }}>
                {user.radUserId}
              </Typography>
            </Box>

            <Box
              sx={{
                pb: 2,
                mb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: "text.secondary",
                  fontSize: "0.65rem",
                  mb: 0.5,
                  display: "block",
                  letterSpacing: "0.05em",
                }}
              >
                FULL NAME
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {user.fullName}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid
          item
          xs={5}
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            position: "relative",
            pb: 1,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "240px",
              height: "160px",
              borderRadius: "20px",
              border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.18,
              transform: "rotate(-8deg)",
              animation: `${float} 4s ease-in-out infinite`,
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                top: "20%",
                right: "10%",
                width: "40%",
                height: "2px",
                background: alpha(theme.palette.primary.main, 0.5),
                boxShadow: `0 12px 0 ${alpha(theme.palette.primary.main, 0.5)}, 0 24px 0 ${alpha(
                  theme.palette.primary.main,
                  0.5,
                )}`,
              },
            }}
          >
            <AssignmentIndIcon sx={{ fontSize: 110, color: "primary.main", opacity: 0.8 }} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const BusinessWidget = ({ user }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: "28px",
        bgcolor: "white",
        border: `2px solid ${alpha(theme.palette.info.main, 0.4)}`,
        boxShadow: `0 12px 40px ${alpha(theme.palette.info.main, 0.15)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          boxShadow: `0 16px 50px ${alpha(theme.palette.info.main, 0.25)}`,
          border: `2px solid ${theme.palette.info.main}`,
          transform: "translateY(-4px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        animation: `${fadeInUp} 0.6s ease-out 0.1s forwards`,
      }}
    >
      <SectionTitle title="Business" icon={DomainIcon} color="info" centered />
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: "text.secondary",
              fontSize: "0.65rem",
              mb: 0.5,
              display: "block",
              letterSpacing: "0.05em",
            }}
          >
            FRANCHISE
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "info.main" }}>
            {user.tenantName || "---"}
          </Typography>
        </Box>

        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: "text.secondary",
              fontSize: "0.65rem",
              mb: 0.5,
              display: "block",
              letterSpacing: "0.05em",
            }}
          >
            BRANCH
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {user.branchName || "---"}
          </Typography>
        </Box>

        <Grid container>
          <Grid
            item
            xs={6}
            sx={{ pr: 2, borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: "text.secondary",
                fontSize: "0.65rem",
                mb: 0.5,
                display: "block",
                letterSpacing: "0.05em",
              }}
            >
              CIRCLE
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {user.state || "Tamilnadu"}
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ pl: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: "text.secondary",
                fontSize: "0.65rem",
                mb: 0.5,
                display: "block",
                letterSpacing: "0.05em",
              }}
            >
              CAF NO
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {user.cafNumber || "---"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export const StatusWidget = ({ user }) => {
  const { activeScheduleUser } = useScheduleUser();
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: "28px",
        bgcolor: "white",
        border: `2px solid ${alpha(theme.palette.warning.main, 0.4)}`,
        boxShadow: `0 12px 40px ${alpha(theme.palette.warning.main, 0.15)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          boxShadow: `0 16px 50px ${alpha(theme.palette.warning.main, 0.25)}`,
          border: `2px solid ${theme.palette.warning.main}`,
          transform: "translateY(-4px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.light} 100%)`,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        animation: `${fadeInUp} 0.6s ease-out 0.2s forwards`,
      }}
    >
      <SectionTitle title="Status" icon={SecurityIcon} color="warning" centered />
      <Grid
        container
        spacing={0}
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Grid item xs={12}>
          <Grid
            container
            sx={{
              mb: 2,
              pb: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            <Grid item xs={6}>
              <Stack spacing={0.8} alignItems="center">
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    letterSpacing: "0.05em",
                  }}
                >
                  ACCOUNT
                </Typography>
                <StatusIndicator status={user.onHoldFromDate ? "HOLD" : user.userStatus} />
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={0.8} alignItems="center">
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    letterSpacing: "0.05em",
                  }}
                >
                  CONNECTIVITY
                </Typography>
                <StatusIndicator status={user.connectionStatus} />
              </Stack>
            </Grid>
          </Grid>

          <Box
            sx={{
              mb: 2,
              pb: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: "text.secondary",
                fontSize: "0.65rem",
                mb: 0.5,
                display: "block",
                letterSpacing: "0.05em",
              }}
            >
              PLAN EXPIRY
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, color: "error.main", fontSize: "0.95rem" }}
            >
              {user.expiration || "---"}
            </Typography>
          </Box>

          {activeScheduleUser.scheduleDateStr != null && (
            <Box
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: "text.secondary",
                  fontSize: "0.65rem",
                  mb: 0.5,
                  display: "block",
                  letterSpacing: "0.05em",
                }}
              >
                SCHEDULED FOR
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "info.main" }}>
                {activeScheduleUser.scheduleDateStr}
              </Typography>
            </Box>
          )}

          {user.onHoldFromDate != null && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: "text.secondary",
                  fontSize: "0.65rem",
                  mb: 0.5,
                  display: "block",
                  letterSpacing: "0.05em",
                }}
              >
                HOLDED PERIOD
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "warning.main" }}>
                {onHoldDate(user.onHoldFromDate, user.onHoldToDate)}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const GeneralInformation = ({ user }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <IdentityWidget user={user} />
        </Grid>
        <Grid item xs={12} md={4}>
          <BusinessWidget user={user} />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatusWidget user={user} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeneralInformation;
